import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from './context.service';
import { map, Observable, of } from 'rxjs';
import {
  WorkflowFieldOption,
  Octopus,
  WorkflowProperty,
  WorkflowListItem,
  WorkflowStartResult,
  WorkflowStartTarget,
  WorkflowCancelResult,
  Entity,
} from '@maximizer/core/shared/domain';

@Injectable()
export class WorkflowService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  private getReadRequest(key?: string): Octopus.WorkflowReadRequest {
    const searchQuery: Octopus.SearchQuery<Octopus.Workflow> = key
      ? {
          Key: {
            $EQ: key,
          },
        }
      : {};
    return {
      Workflow: {
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            Description: 1,
            Category: [
              {
                Key: 1,
                DisplayValue: 1,
              },
            ],
            EntityType: {
              Key: 1,
              DisplayValue: 1,
            },
            SecStatus: {
              CanRead: 1,
              CanCreate: 1,
              CanUpdate: 1,
              CanDelete: 1,
            },
            IsSequential: 1,
            HasStages: 1,
            SecAccess: {
              Read: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
              Write: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                  DisplayValue: 1,
                },
              ],
            },
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
    };
  }

  read(key?: string): Observable<Entity[]> {
    const request = this.getReadRequest(key);

    return this.http
      .post<Octopus.EntityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Workflow?.Data as Entity[];
          }
          return [];
        }),
      );
  }

  readByKeys(keys: string[]): Observable<WorkflowProperty[]> {
    if (!keys || keys.length === 0) {
      return of([]);
    }

    const uniqueKeys = Array.from(new Set(keys));

    const searchQuery: Octopus.SearchQuery<Octopus.Workflow> = {
      Key: {
        $IN: uniqueKeys,
      },
    };

    const request: Octopus.WorkflowReadRequest = {
      Workflow: {
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            HasStages: 1,
            EntityType: {
              Key: 1,
              DisplayValue: 1,
            },
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowPropertyMapper();
            return mapper.from(result.Workflow.Data);
          }
          return [];
        }),
      );
  }

  readForGrid(key?: string): Observable<WorkflowProperty[]> {
    const request = this.getReadRequest(key);

    return this.http
      .post<Octopus.WorkflowReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowPropertyMapper();
            return mapper.from(result.Workflow?.Data ?? []);
          }
          return [];
        }),
      );
  }

  getWorkflowList(
    entityType?: Octopus.EntityType,
  ): Observable<WorkflowListItem[]> {
    const request: Octopus.WorkflowReadRequest = {
      Workflow: {
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            HasStages: 1,
            IsSequential: 1,
            SecStatus: {
              CanDelete: 1,
            },
            EntityType: {
              Key: 1,
              DisplayValue: 1,
            },
          },
        },
        Criteria: {
          SearchQuery:
            entityType != null
              ? { EntityType: { $EQ: entityType } }
              : {},
        },
        OrderBy: {
          Fields: [{ Name: 'ASC' }],
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowPropertyMapper();
            return mapper.mapToListItem(result.Workflow.Data);
          }
          return [];
        }),
      );
  }

  getFieldOptions(): Observable<WorkflowFieldOption> {
    const request: Octopus.WorkflowFieldOptionsRequest = {
      Workflow: {
        FieldOptions: {
          SecAccess: {
            Read: [
              {
                Key: {
                  Uid: 1,
                  Value: 1,
                },
                DisplayValue: 1,
              },
            ],
            Write: [
              {
                Key: {
                  Uid: 1,
                  Value: 1,
                },
                DisplayValue: 1,
              },
            ],
          },
          Category: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          EntityType: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowFieldOptionMapper();
            return mapper.from(result);
          }
          return { readAccess: [], fullAccess: [], category: [], entity: [] };
        }),
      );
  }

  update(values: Entity): Observable<string | null> {
    const request = {
      Workflow: {
        Data: values,
      },
      Globalization: Octopus.DefaultGlobalization,
    };
    return this.http
      .post<Octopus.WorkflowWriteResponse>(
        `${this.context.api}${values[Octopus.EntityFields.Key] ? Octopus.Action.UPDATE : Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Workflow.Data.Key;
          }
          return null;
        }),
      );
  }

  delete(key: string): Observable<boolean> {
    const request = {
      Workflow: {
        Data: {
          Key: key,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowWriteResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return true;
          }
          return false;
        }),
      );
  }

  workflowStart(
    workflowKey: string,
    parentKeys: (string | WorkflowStartTarget)[],
  ): Observable<WorkflowStartResult> {
    const request = (parentKeys ?? []).reduce<Record<string, unknown>>(
      (result, target, index) => {
        const { parentKey, withKey } =
          typeof target === 'string' ? { parentKey: target, withKey: undefined } : target;

        result[`Workflow.${index}`] = {
          Data: {
            Key: workflowKey,
            ParentKey: parentKey,
            ...(withKey ? { WithKey: withKey } : {}),
          },
        };
        return result;
      },
      { Globalization: Octopus.DefaultGlobalization },
    );

    return this.http
      .post<Octopus.WorkflowWriteResponse>(
        `${this.context.api}${Octopus.Action.WorkflowStart}`,
        request,
      )
      .pipe(
        map((result) => ({
          success: result.Code === Octopus.ResponseStatusCode.Successful,
          errorCode: this.getResponseErrorCode(result),
        })),
      );
  }

  cancelWorkflow(
    workflowKey: string,
    parentKey: string,
    withKey?: string | null,
    workflowStageKey?: string | null,
  ): Observable<WorkflowCancelResult> {
    const request = {
      Workflow: {
        Data: {
          Key: workflowKey,
          ParentKey: parentKey,
          ...(withKey ? { WithKey: withKey } : {}),
          ...(workflowStageKey ? { WorkflowStageKey: workflowStageKey } : {}),
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.WorkflowWriteResponse>(
        `${this.context.api}${Octopus.Action.CancelWorkflow}`,
        request,
      )
      .pipe(
        map((result) => ({
          success: result.Code === Octopus.ResponseStatusCode.Successful,
          errorCode: this.getResponseErrorCode(result),
        })),
      );
  }

  private getResponseErrorCode(
    response: Octopus.WorkflowWriteResponse,
  ): number | null {
    const messages = response.Msg ?? [];
    const error = messages.find(
      (message): message is Octopus.ResponseError =>
        typeof message !== 'string' && message.ErrorCode != null,
    );
    return error?.ErrorCode ?? null;
  }

  checkDuplicated(name: string): Observable<boolean> {
    const request: Octopus.WorkflowReadRequest = {
      Workflow: {
        Scope: {
          Fields: {
            Key: 1,
          },
        },
        Criteria: {
          SearchQuery: {
            Name: {
              $EQ: name,
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Workflow?.Data?.length > 0;
          }
          return false;
        }),
      );
  }
}
